import React, { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Title, Button } from '@lib'
import { object, axiosGet } from '@utils'
import { Form, Group, Input, Submit, FormError } from '@shared/Form'
import axios from 'axios'
import Dashboard from '@wrappers/Dashboard'
import Swal from 'sweetalert2'
import config from '@config'
import createResource from '@graphql/mutators/create-resource'

const validateMagicBytes = (file, type) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const b = new Uint8Array(reader.result)
      if (type === 'application/pdf') {
        resolve(b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46)
      } else if (type === 'video/mp4') {
        resolve(b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70)
      } else {
        resolve(false)
      }
    }
    reader.readAsArrayBuffer(file.slice(0, 12))
  })
}

const getBucketEnv = () => {
  if (config.APP_ENV === 'prod' || config.APP_ENV === 'production') return 'prod'
  if (config.APP_ENV === 'staging' || config.APP_ENV === 'stage') return 'stage'
  return 'dev'
}

const Content = ({ user }) => {
  const navigate = useNavigate()
  const [inputErrors, setInputErrors] = useState({})
  const [input, setInput] = useState({ videoFile: null, pdfFile: null, name: null, summary: null, image: null })
  const [loaderState, setLoaderState] = useState({ submit: false })
  const [disabled, setDisabled] = useState(false)

  const handleInputChange = (field, v) => {
    setInputErrors(prev => ({ ...prev, [field]: [] }))
    setInput(prev => ({ ...prev, [field]: v }))
  }

  const handleUpload = async (evt) => {
    if (evt) evt.preventDefault()
    setDisabled(true)
    const feErrors = {}

    if (!input.name) feErrors.name = [{ message: 'Course name is required' }]
    if (!input.videoFile && !input.pdfFile) feErrors.file = [{ message: 'At least one file (MP4 or PDF) is required' }]

    if (input.videoFile instanceof File) {
      if (input.videoFile.type !== 'video/mp4') {
        feErrors.videoFile = [{ message: 'Video must be of type .mp4' }]
      } else {
        const valid = await validateMagicBytes(input.videoFile, 'video/mp4')
        if (!valid) feErrors.videoFile = [{ message: 'File content does not match .mp4 format' }]
      }
    }

    if (input.pdfFile instanceof File) {
      if (input.pdfFile.type !== 'application/pdf') {
        feErrors.pdfFile = [{ message: 'Document must be of type .pdf' }]
      } else {
        const valid = await validateMagicBytes(input.pdfFile, 'application/pdf')
        if (!valid) feErrors.pdfFile = [{ message: 'File content does not match .pdf format' }]
      }
    }

    if (input.image instanceof File && !['image/jpeg', 'image/png'].includes(input.image.type)) {
      feErrors.imagetype = [{ message: 'Thumbnail must be of type .png, .jpg, .jpeg' }]
    }

    if (!object.keys(feErrors).length) {
      setLoaderState(prev => ({ ...prev, submit: true }))

      const newInput = { name: input.name, summary: input.summary }
      if (input.videoFile instanceof File) {
        newInput.videoFile = JSON.stringify({ originalname: input.videoFile.name, mimetype: input.videoFile.type, size: input.videoFile.size })
      }
      if (input.pdfFile instanceof File) {
        newInput.pdfFile = JSON.stringify({ originalname: input.pdfFile.name, mimetype: input.pdfFile.type, size: input.pdfFile.size })
      }
      if (input.image instanceof File) {
        newInput.image = JSON.stringify({ originalname: input.image.name, mimetype: input.image.type, size: input.image.size })
      }

      createResource(newInput).then(async response => {
        if (response.data.createResource) {
          const bucket = `xevant-${getBucketEnv()}-alcatraz-be-uploads-us-west-2`
          const res = response.data.createResource

          const uploads = []
          if (input.videoFile instanceof File && res.videoFileName) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('mediaFiles/' + res.videoFileName)}?contentType=video%2Fmp4`).then(e => axios.put(e.data.url, input.videoFile, { headers: { 'Content-Type': 'video/mp4' } })))
          }
          if (input.pdfFile instanceof File && res.pdfFileName) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('mediaFiles/' + res.pdfFileName)}?contentType=application%2Fpdf`).then(e => axios.put(e.data.url, input.pdfFile, { headers: { 'Content-Type': 'application/pdf' } })))
          }
          if (input.image instanceof File && res.image) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('resourceUploads/images/' + res.image)}`).then(e => axios.put(e.data.url, input.image)))
          }

          await Promise.all(uploads)
          Swal.fire({ text: 'Course successfully created', icon: 'success', showConfirmButton: false, timer: 3000 })
          navigate('/settings/training-courses')
        }
      })
    } else {
      setInputErrors(feErrors)
      setDisabled(false)
    }
  }

  return (
    <Fragment>
      <Title text='New Training Course' user={user}/>

      <Form className='course-upload-form' onSubmit={handleUpload}>
        <FormError errors={inputErrors} />
        <Group>
          <Input
            label='Course Name'
            type='text'
            value={input.name}
            onChange={(v) => handleInputChange('name', v)}
            errors={inputErrors.name}
          />
        </Group>
        <div className={`course-files-card${inputErrors.file ? ' has-error' : ''}`}>
          <div className='course-files-card-header'>
            <span className='course-files-label'>Course Content <span className='required'>*</span></span>
            <span className='course-files-hint'>At least one is required. Both can be uploaded.</span>
          </div>
          {inputErrors.file && <p className='course-files-error'>{inputErrors.file[0].message}</p>}
          <div className='course-files-row'>
            <Group>
              <Input
                label='Upload Video'
                type='draganddrop'
                placeholder='upload as .mp4'
                value={input.videoFile}
                onChange={(v) => handleInputChange('videoFile', v.target.files[0])}
                accept='.mp4'
                className='resource-upload'
                errors={inputErrors.videoFile}
              />
            </Group>
            <Group>
              <Input
                label='Upload PDF'
                type='draganddrop'
                placeholder='upload as .pdf'
                value={input.pdfFile}
                onChange={(v) => handleInputChange('pdfFile', v.target.files[0])}
                accept='.pdf'
                className='resource-upload'
                errors={inputErrors.pdfFile}
              />
            </Group>
          </div>
        </div>

        <Group>
          <Input
            label={<>Upload Thumbnail Image <span className='optional'>(Optional)</span></>}
            type='draganddrop'
            value={input.image}
            onChange={(v) => handleInputChange('image', v.target.files[0])}
            accept='.png, .jpg, .jpeg'
            logoDirectory='/resourceUploads/images/'
            className='thumbnail-upload'
            errors={inputErrors.imagetype}
          />
        </Group>
        <Group>
          <Input
            label={<>Course Summary <span className='optional'>(Optional)</span></>}
            type='textarea'
            value={input.summary}
            onChange={(v) => handleInputChange('summary', v)}
          />
        </Group>

        <Submit>
          <Button
            type='submit'
            text='Upload Course'
            icon='fas fa-arrow-alt-to-top'
            iconAlign='left'
            className='icon-left primary full-width'
            loading={loaderState.submit}
            disabled={disabled}
          />
        </Submit>
      </Form>
    </Fragment>
  )
}

Content.propTypes = {
  user: PropTypes.object
}

const UploadCourse = () => {
  return (
    <Dashboard>
      <Content/>
    </Dashboard>
  )
}

export default UploadCourse
